import React, { useState } from "react";
import { toast } from "react-toastify";
import SlidePanel from "../../../components/SlidePanel/SlidePanel";
import * as hrmsService from "../../../services/hrmsService";
import { EmptyState, LoadState, PageHeader, SecondaryButton } from "../hrComponents";
import { cardStyle, formatDate, formatRange, pageStyle, tdStyle, thStyle, useLoad } from "../hrUi";
import { CURRENCY, formatMoney } from "./payrollHelpers";

// PAY-07: my payslips. The PDF is password protected; the screen says what the password is (never the password itself).
const PASSWORD_HINT = {
  DOB: "your date of birth as DDMMYYYY (for example 07031990)",
  EMPLOYEE_CODE: "your employee code",
  PHONE_LAST4: "the last 4 digits of your work phone number",
};

function PayslipDrawer({ id, onHide }) {
  const { data, loading, error, reload } = useLoad(() => hrmsService.getPayslip(id), [id], { enabled: !!id });
  const lines = data?.lines || [];
  const part = (kind) => lines.filter((l) => l.kind === kind);
  const table = (title, rows) =>
    rows.length ? (
      <div className="mb-3">
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{title}</div>
        <table className="table mb-0" style={{ fontSize: 13 }}>
          <tbody>
            {rows.map((l, i) => (
              <tr key={i}>
                <td style={tdStyle}>
                  {l.component_name}
                  {l.note ? <div style={{ fontSize: 11, color: "#6B7280" }}>{l.note}</div> : null}
                </td>
                <td style={{ ...tdStyle, textAlign: "right" }}>{formatMoney(l.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : null;
  return (
    <SlidePanel show={!!id} onHide={onHide} title={data?.title || "Payslip"} width={480}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {data ? (
          <>
            <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 12 }}>
              {formatRange(data.period_start, data.period_end)}
              {data.pay_date ? ` · pay date ${formatDate(data.pay_date)}` : ""} · {data.paid_days} of {data.days_basis_value} days paid
            </div>
            {table("Earnings", part("EARNING"))}
            {table("Deductions", part("DEDUCTION"))}
            {table("Reimbursements", part("REIMBURSEMENT"))}
            <div className="d-flex justify-content-between" style={{ background: "#F1F4F8", borderRadius: 8, padding: "12px 14px", fontWeight: 700 }}>
              <span>Net pay</span>
              <span>{formatMoney(data.net)}</span>
            </div>
          </>
        ) : null}
      </LoadState>
    </SlidePanel>
  );
}

export default function MyPayslips({ country }) {
  const { data, loading, error, reload } = useLoad(() => hrmsService.listMyPayslips(), []);
  const [viewing, setViewing] = useState(null);
  const rows = data?.payslips || [];
  const hint = PASSWORD_HINT[data?.password_field];
  const currency = CURRENCY[country] || "";

  const download = async (id) => {
    try {
      const res = await hrmsService.getPayslipDownload(id);
      if (res?.data?.url) window.open(res.data.url, "_blank", "noopener");
      else toast.error(hrmsService.responseMessage(res, "The PDF is not ready yet"));
    } catch (err) {
      toast.error("The PDF is not ready yet");
    }
  };

  return (
    <div style={pageStyle}>
      <PageHeader title="My payslips" subtitle={hint ? `PDFs open with a password: ${hint}.` : undefined} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No payslips yet" text="Your payslips appear here when payroll is published." />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0">
              <thead>
                <tr>
                  {["Period", `Net pay ${currency}`, ""].map((h, i) => (
                    <th key={i} style={{ ...thStyle, textAlign: i === 1 ? "right" : "left", ...(i > 0 ? { width: "1%", whiteSpace: "nowrap" } : null) }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id}>
                    <td style={tdStyle}>
                      {p.title}
                      <div style={{ fontSize: 12, color: "#6B7280" }}>{formatRange(p.period_start, p.period_end)}</div>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", width: "1%", whiteSpace: "nowrap" }}>{formatMoney(p.net)}</td>
                    <td style={{ ...tdStyle, width: "1%", whiteSpace: "nowrap" }}>
                      <div className="d-flex gap-2 justify-content-end">
                        <SecondaryButton onClick={() => setViewing(p.id)}>View</SecondaryButton>
                        <SecondaryButton onClick={() => download(p.id)} disabled={!p.has_file}>
                          {p.has_file ? "Download PDF" : "PDF being made"}
                        </SecondaryButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <PayslipDrawer id={viewing} onHide={() => setViewing(null)} />
    </div>
  );
}
